// AGC: AppDrawer - Left side drawer navigation with hamburger menu
// AGC: Replaces TopNav, provides grouped navigation + profile access
// AGC: Full-screen overlay drawer, mobile-first, accessibility-focused

import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  // Profile/Account
  User,
  CreditCard,
  Settings as SettingsIcon,
  LogOut,
  // Operations
  CheckSquare,
  MapPin,
  Wrench,
  Package,
  DollarSign,
  // Team
  Users,
  Shield,
  Key,
  Trophy,
  // UI
  Home,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react'
import type { Screen } from '../../types'
import { useTranslation } from '../../hooks/useTranslation'
import { useRBAC } from '../../hooks/useRBAC'
import { APP_VERSION } from '@droidfarm/shared'
import { getBackendUrl } from '../../utils/backendUrl'

interface AppDrawerProps {
  isOpen: boolean
  onClose: () => void
  currentScreen: Screen
  onNavigate: (screen: Screen) => void
  userName: string
  userAvatar?: string
  userEmail?: string
  appVersion?: string
  isOnline?: boolean
  // AGC: Desktop shell: when set, overlay and panel start at this left offset (px) so the sidebar stays uncovered
  desktopOffsetLeft?: number
}

interface NavItem {
  id: Screen
  label: string
  icon: React.ReactNode
  badge?: string | number
}

interface NavSection {
  heading: string
  items: NavItem[]
}

interface NavGroup {
  id: string
  label: string
  icon: React.ReactNode
  items: NavItem[]
}

/**
 * AGC: AppDrawer - Left side navigation drawer
 * AGC: Features:
 * AGC: - Profile card at top (click → profile page)
 * AGC: - Grouped navigation sections
 * AGC: - Collapsible Team submenu
 * AGC: - Active state highlighting
 * AGC: - Focus trap when open
 * AGC: - ESC to close
 * AGC: - Overlay click to close
 * AGC: - Sync/offline indicator
 */
export const AppDrawer: React.FC<AppDrawerProps> = ({
  isOpen,
  onClose,
  currentScreen,
  onNavigate,
  userName,
  userAvatar,
  userEmail,
  appVersion = APP_VERSION,
  isOnline = true,
  desktopOffsetLeft,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const rbac = useRBAC()

  // AGC: Fetch backend version from /health endpoint (once when drawer opens)
  const [backendVersion, setBackendVersion] = useState<string | null>(null)
  useEffect(() => {
    if (!isOpen) return
    fetch(`${getBackendUrl()}/health`)
      .then(r => r.json())
      .then(d => setBackendVersion(d.version || null))
      .catch(() => setBackendVersion(null))
  }, [isOpen])

  // AGC: Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  // AGC: Focus trap when drawer opens
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      }
    }
  }, [isOpen])

  // AGC: Prevent body scroll when drawer open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // AGC: Navigation sections — labels use t() for i18n
  const accountSection: NavSection = {
    heading: t('nav.account'),
    items: [
      { id: 'profile', label: t('nav.profile'), icon: <User className="w-5 h-5" /> },
      { id: 'subscription', label: t('nav.plans'), icon: <CreditCard className="w-5 h-5" /> },
      { id: 'settings', label: t('nav.settings'), icon: <SettingsIcon className="w-5 h-5" /> },
    ],
  }

  // AGC: "My Farm" section — filter items by RBAC permissions (6-role system)
  // AGC: owner: all | manager: all except admin | agronomist: fields+tasks, no financial
  // AGC: financial: financial+costs, no fields edit | worker: tasks status only | viewer: read-only
  const rp = rbac.permissions
  const farmSection: NavSection = useMemo(() => {
    const items: NavItem[] = []
    // AGC: Fields/Lots — visible to all except viewer (who can still see via dashboard)
    items.push({ id: 'lotes-list', label: t('nav.lots'), icon: <MapPin className="w-5 h-5" /> })
    // AGC: Machinery — visible to owner, manager (full), agronomist (view), financial (costs only)
    if (rp?.canCreateMachine || rp?.canViewMachineCosts || rbac.isOwner || rbac.isManager) {
      items.push({ id: 'machinery', label: t('nav.machinery'), icon: <Wrench className="w-5 h-5" /> })
    }
    // AGC: Inventory — visible to roles that can consume or manage
    if (rp?.canConsumeInventory || rp?.canManageInventory || rbac.isOwner || rbac.isManager) {
      items.push({ id: 'inventory', label: t('nav.inventory'), icon: <Package className="w-5 h-5" /> })
    }
    // AGC: Finances — only roles with canViewFinancial
    if (rp?.canViewFinancial) {
      items.push({ id: 'finances', label: t('nav.finances'), icon: <DollarSign className="w-5 h-5" /> })
    }
    // AGC: Team — visible to roles that can invite or view team
    if (rp?.canViewTeam) {
      items.push({ id: 'team-users', label: t('nav.team'), icon: <Users className="w-5 h-5" /> })
    }
    // AGC: Sorteios — visible to all users
    items.push({ id: 'sorteios', label: t('sorteio.page.title'), icon: <Trophy className="w-5 h-5" /> })
    return { heading: t('nav.myFarm'), items }
  }, [rp, rbac.isOwner, rbac.isManager, t])

  // AGC: Handle navigation (close drawer after navigating)
  const handleNavigate = (screen: Screen) => {
    onNavigate(screen)
    onClose()
  }

  // AGC: Render nav item
  // AGC: Use inline style for active background/text to guarantee visibility regardless of Tailwind JIT purging
  const renderNavItem = (item: NavItem, isSubmenu = false) => {
    const isActive = currentScreen === item.id
    
    return (
      <button
        key={item.id}
        onClick={() => handleNavigate(item.id)}
        className={`
          w-full flex items-center gap-3 px-4 py-3 rounded-lg
          transition-all duration-200
          ${isSubmenu ? 'pl-12' : ''}
          ${isActive
            ? ''
            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          }
        `}
        style={isActive ? { backgroundColor: '#065F46', color: '#FFFFFF' } : undefined}
        aria-current={isActive ? 'page' : undefined}
      >
        {item.icon}
        <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
        {isActive && (
          <span className="w-2 h-2 rounded-full shadow-lg" style={{ backgroundColor: '#FFDF00' }} />
        )}
        {item.badge && (
          <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-semibold rounded-full">
            {item.badge}
          </span>
        )}
      </button>
    )
  }

  // AGC: Render section
  const renderSection = (section: NavSection) => (
    <div key={section.heading} className="space-y-1">
      <h3 className="px-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
        {section.heading}
      </h3>
      {section.items.map(item => renderNavItem(item))}
    </div>
  )

  if (!isOpen) return null

  // AGC: On desktop, overlay and panel start at desktopOffsetLeft so the sidebar stays uncovered
  const offsetStyle = desktopOffsetLeft ? { left: `${desktopOffsetLeft}px` } : undefined

  return (
    <>
      {/* AGC: Overlay */}
      <div
        className="fixed top-0 bottom-0 right-0 bg-black/50 z-[90] animate-in fade-in duration-200"
        style={offsetStyle}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* AGC: Drawer Panel */}
      <div
        ref={drawerRef}
        className="fixed top-0 bottom-0 w-[220px] bg-white dark:bg-slate-900 z-[90] shadow-2xl flex flex-col animate-in slide-in-from-left duration-200"
        style={offsetStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* AGC: Close button — sticks out to the RIGHT of the drawer as a tab handle
            so users can tap the visible target without hitting menu items. Positioned at
            drawer-width (220px) with negative margin to overlap a pixel for seamless look. */}
        <button
          onClick={onClose}
          className="absolute p-3 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white shadow-lg rounded-r-xl transition-colors z-[95] border border-l-0 border-slate-200 dark:border-slate-700"
          style={{
            left: '220px',
            top: 'max(calc(env(safe-area-inset-top, 0px) + 1rem), 2.25rem)',
          }}
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>

        {/* AGC: Profile Card Header — max() ensures it clears the Android status bar even when env() returns 0 */}
        <div
          className="px-3 pb-3 border-b border-slate-200 dark:border-slate-700"
          style={{ paddingTop: 'max(calc(env(safe-area-inset-top, 0px) + 1rem), 2.25rem)' }}
        >
          <button
            onClick={() => handleNavigate('profile')}
            className="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
            aria-label="View profile"
          >
            {/* AGC: Avatar */}
            {userAvatar ? (
              <img
                src={userAvatar}
                alt={userName}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-brand-primary flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center ring-2 ring-brand-primary flex-shrink-0">
                <span className="text-white font-semibold text-base">
                  {userName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}

            {/* AGC: Name + Email */}
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                {userName}
              </p>
              {userEmail && (
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {userEmail}
                </p>
              )}
              <p className="text-xs text-brand-primary dark:text-brand-light font-medium mt-0.5">
                {t('nav.viewProfile')}
              </p>
            </div>
          </button>
        </div>

        {/* AGC: Navigation Sections */}
        {/* AGC: On desktop (desktopOffsetLeft set) show only account section — farm items live in DesktopSidebar */}
        {/* AGC: On mobile show both sections as before (no behavioral change for Capacitor) */}
        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {renderSection(accountSection)}
          {!desktopOffsetLeft && renderSection(farmSection)}
        </div>

        {/* AGC: Footer — max() ensures it clears the Android nav bar even when env() returns 0 */}
        <div
          className="border-t border-slate-200 dark:border-slate-700 px-4 pt-4 space-y-3"
          style={{ paddingBottom: 'max(calc(env(safe-area-inset-bottom, 0px) + 1rem), 1.5rem)' }}
        >
          {/* Sync/Offline Indicator */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800">
            {isOnline ? (
              <>
                <Wifi className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {t('nav.online')}
                </span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {t('nav.offline')}
                </span>
              </>
            )}
          </div>

          {/* App Version — frontend + backend */}
          <div className="text-center text-xs text-slate-400 dark:text-slate-500 space-y-0.5">
            <div>App v{appVersion}</div>
            <div>Backend v{backendVersion || '...'}</div>
          </div>

          {/* Logout Button (optional, uncomment if needed) */}
          {/* <button
            onClick={() => {
              // Handle logout
              onClose()
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Sair</span>
          </button> */}
        </div>
      </div>
    </>
  )
}
